import isEqual from 'lodash/isEqual'
import { EventListPageType } from '~/composables/types/strapiEventListPage'

export const pageTypeHasStructuralLocation = (
  pageType: EventListPageType
): boolean =>
  pageType === EventListPageType.CITY ||
  pageType === EventListPageType.CITY_CATEGORY

export const pageTypeHasStructuralCategory = (
  pageType: EventListPageType
): boolean =>
  pageType === EventListPageType.CATEGORY ||
  pageType === EventListPageType.CITY_CATEGORY

/**
 * True when location or categories changed compared to the previous watcher tick.
 */
export const transactionalFilterStructuralFieldsChanged = (
  newLocation: unknown,
  oldLocation: unknown,
  newCategories: unknown,
  oldCategories: unknown
): boolean => {
  const categoriesArray = Array.isArray(newCategories) ? newCategories : []
  const oldCategoriesArray = Array.isArray(oldCategories) ? oldCategories : []
  return (
    newLocation !== oldLocation || !isEqual(categoriesArray, oldCategoriesArray)
  )
}

/**
 * When structural filters are invalid for a transactional page, skip Strapi
 * and go straight to search (see FilteredProductShowcase watcher).
 *
 * Category-structural pages: only **multiple** selected categories force search.
 * Zero categories still run the Strapi check (route slug remains the anchor).
 * Teamevent: no selected location → search (cannot build a teamevent Strapi slug).
 */
export const computeTransactionalFilterForceSearch = (
  pageType: EventListPageType,
  location: string | undefined,
  categories: unknown[]
): boolean => {
  const pageHasLocation = pageTypeHasStructuralLocation(pageType)
  const pageHasCategory = pageTypeHasStructuralCategory(pageType)
  return (
    (pageHasCategory && categories.length > 1) ||
    (pageHasLocation && !location) ||
    (pageType === EventListPageType.TEAMEVENT && !location)
  )
}

export interface StrapiCategorySlugResolutionInput {
  categories: unknown[]
  nameToSlug: Record<string, string>
  routeCategorySlug?: string
}

/**
 * Strapi `categorySlug` for the existence check: map a single selected display
 * name to its store slug via Algolia hits; otherwise use the route slug.
 */
export const resolveStrapiCategorySlugForNavigation = (
  options: StrapiCategorySlugResolutionInput
): string | undefined => {
  const { categories, nameToSlug, routeCategorySlug } = options
  if (categories.length === 1) {
    const name = String(categories[0])
    return nameToSlug[name] ?? routeCategorySlug
  }
  return routeCategorySlug
}
